import { useMemo } from 'react';
import {
  Line,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCount, formatPct, prettyQuarter } from '@/lib/format';
import {
  type TitleRevenueRow,
  buildShareSeries,
  primaryScope,
  latestQuarter,
  SHARE_COLORS,
} from '@/lib/titleCharts';
import { ChartSummary, Emph } from './ChartSummary';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import styles from './AcquisitionRetentionChart.module.css';

export interface AcquisitionRetentionChartProps {
  id: string;
  title: string;
  revenue: TitleRevenueRow[];
}

/**
 * Acquisition & Retention Impact — ported from d360's AcquisitionRetentionWidget,
 * rebuilt in Recharts. Two share-of-users lines over time (retention = purple,
 * acquisition = teal), scoped to the title's primary platform+region (the d360
 * default), with a narrative summary built from live data.
 */
export function AcquisitionRetentionChart({ title, revenue }: AcquisitionRetentionChartProps) {
  const { platform, region } = useMemo(() => primaryScope(revenue), [revenue]);
  const points = useMemo(() => buildShareSeries(revenue, platform, region), [revenue, platform, region]);

  const quarter = latestQuarter(revenue);
  const latest = points.find((p) => p.quarter === quarter) ?? points[points.length - 1];

  const hasData = points.some((p) => p.retention !== null || p.acquisition !== null);

  // Resolve token strings to concrete colours for the SVG strokes/gradients.
  const [retentionColor, acquisitionColor] = useResolvedColors([
    SHARE_COLORS.retention,
    SHARE_COLORS.acquisition,
  ]);

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div>
          <h3 className={styles.title}>Acquisition &amp; Retention Impact</h3>
          <span className={styles.sub}>
            Share of users over time{platform ? ` · ${platform}` : ''}{region ? ` · ${region}` : ''}
          </span>
        </div>
        <ChartLegend
          placement="header"
          items={[
            { label: 'Retention', color: SHARE_COLORS.retention },
            { label: 'Acquisition', color: SHARE_COLORS.acquisition },
          ]}
        />
      </header>

      <ChartSummary>
        <Emph>{title}</Emph> drove{' '}
        <Emph tone="purple">{latest?.retention != null ? formatPct(latest.retention, 2) : '—'}</Emph> of{' '}
        {platform ? <Emph>{platform}</Emph> : 'platform'}'s{' '}
        <Emph tone="purple">user retention</Emph>
        {latest?.retainedSubs ? (
          <>
            {' '}(<Emph>{formatCount(latest.retainedSubs)}</Emph> users)
          </>
        ) : null}{' '}
        and contributed{' '}
        <Emph tone="teal">{latest?.acquisition != null ? formatPct(latest.acquisition, 2) : '—'}</Emph> of new{' '}
        <Emph tone="teal">user acquisition</Emph>
        {latest?.acquiredSubs ? (
          <>
            {' '}(<Emph>{formatCount(latest.acquiredSubs)}</Emph> users)
          </>
        ) : null}
        {region ? (
          <>
            {' '}in <Emph>{region}</Emph>
          </>
        ) : null}{' '}
        during <Emph>{quarter ? prettyQuarter(quarter) : '—'}</Emph>.
      </ChartSummary>

      {hasData ? (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id="retentionGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={retentionColor} stopOpacity={0.25} />
                <stop offset="100%" stopColor={retentionColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="acquisitionGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={acquisitionColor} stopOpacity={0.25} />
                <stop offset="100%" stopColor={acquisitionColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--border-subtle)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={44}
              tickFormatter={(v) => `${Number(v).toFixed(1)}%`}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload as (typeof points)[number] | undefined;
                if (!p) return null;
                return (
                  <ChartTooltip
                    title={label}
                    rows={[
                      { label: 'Retention', value: p.retention != null ? formatPct(p.retention, 2) : '—', color: SHARE_COLORS.retention },
                      { label: 'Acquisition', value: p.acquisition != null ? formatPct(p.acquisition, 2) : '—', color: SHARE_COLORS.acquisition },
                    ]}
                  />
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="retention"
              stroke="none"
              fill="url(#retentionGrad)"
              connectNulls
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="acquisition"
              stroke="none"
              fill="url(#acquisitionGrad)"
              connectNulls
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="retention"
              stroke={retentionColor}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="acquisition"
              stroke={acquisitionColor}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className={styles.empty}>No acquisition/retention data available</div>
      )}
    </section>
  );
}
